import React from "react";
import {
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Button,
    Row,
    Col,
    Card,
    CardBody,
    Badge,
} from "reactstrap";
import "./ClassDetailsModal.css"; // Custom CSS for additional styling

const ClassDetailsModal = ({ isOpen, toggle, classData }) => {
    const FILE_BASE_URL = process.env.REACT_APP_FILE_BASE_URL || process.env.REACT_APP_BASE_URL || "http://localhost:4001";

    if (!classData) {
        return;
    }

    return (
        <Modal
            isOpen={isOpen}
            toggle={toggle}
            centered
            size="lg"
            className="class-details-modal"
            style={{ maxWidth: "800px" }}
        >
            <ModalHeader
                toggle={toggle}
                className="bg-primary text-white border-0"
                style={{
                    borderTopLeftRadius: "10px",
                    borderTopRightRadius: "10px",
                    padding: "1.5rem",
                }}
            >
                <h4 className="mb-0">{classData.title}</h4>
                {classData.titleAr && (
                    <h5 className="mb-0 mt-1" style={{ fontSize: "1rem", opacity: 0.8 }}>
                        {classData.titleAr}
                    </h5>
                )}
            </ModalHeader>
            <ModalBody className="p-4" style={{ backgroundColor: "#f8f9fa", maxHeight: "60vh", overflow: "auto" }}>
                <Row>
                    {/* Cover Photo */}
                    <Col md="6" className="mb-3">
                        <img
                            src={`${FILE_BASE_URL}/${classData.coverPhoto}`}
                            alt={classData.title}
                            className="img-fluid rounded shadow-sm"
                            style={{
                                width: "100%",
                                maxHeight: "250px",
                                objectFit: "cover",
                                border: "2px solid #e9ecef",
                            }}
                        />
                    </Col>
                    {/* Class Details */}
                    <Col md="6">
                        <Card className="border-0 shadow-sm">
                            <CardBody>
                                <h5 className="text-primary mb-3">Class Details</h5>
                                <p>
                                    <strong>Price:</strong> {classData.price} KWD
                                </p>
                                <p>
                                    <strong>Duration:</strong> {classData.duration} minutes
                                </p>
                                <p>
                                    <strong>Class Type:</strong> {classData.type} minutes
                                </p>
                                <p>
                                    <strong>Date/Time:</strong>{" "}
                                    {(() => {
                                        const rawDate = classData.date;

                                        if (!rawDate || typeof rawDate !== "string") return "N/A";

                                        const trimmed = rawDate.trim();

                                        // Match formats like "14:30", "9:00 AM", etc.
                                        const isTimeOnly = /^(\d{1,2}):(\d{2})(\s?[APMapm]{2})?$/.test(trimmed);

                                        if (isTimeOnly) return trimmed;

                                        const parsed = new Date(trimmed);
                                        if (isNaN(parsed.getTime())) return "Invalid Date";

                                        return parsed.toLocaleString("en-US", {
                                            dateStyle: "medium",
                                            timeStyle: "short",
                                        });
                                    })()}
                                </p>
                                <p>
                                    <strong>Capacity:</strong> {classData.capacity} participants
                                </p>
                                <p>
                                    <strong>Coach:</strong> {classData.coach?.name || "N/A"}
                                </p>
                                {classData.type === "schedule" && (
                                    <div>
                                        <strong>Schedule:</strong>
                                        <div className="mt-2 d-flex flex-wrap">
                                            {classData?.schedule?.map((scd, index) => (
                                                <span
                                                    key={index}
                                                    style={{
                                                        backgroundColor: "#33bdbf",
                                                        color: "#ffffff",
                                                        padding: "6px 12px",
                                                        borderRadius: "999px",
                                                        fontSize: "0.85rem",
                                                        fontWeight: 600,
                                                        marginRight: "8px",
                                                        marginBottom: "8px",
                                                        display: "inline-block",
                                                    }}
                                                >
                                                    {scd}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                <div>
                                    <strong>Aims:</strong>
                                    <div className="mt-2 d-flex flex-wrap">
                                        {classData?.aim?.map((aim, index) => (
                                            <span
                                                key={index}
                                                style={{
                                                    backgroundColor: "#33bdbf",
                                                    color: "#ffffff",
                                                    padding: "6px 12px",
                                                    borderRadius: "999px",
                                                    fontSize: "0.85rem",
                                                    fontWeight: 600,
                                                    marginRight: "8px",
                                                    marginBottom: "8px",
                                                    display: "inline-block",
                                                }}
                                            >
                                                {aim}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
                {/* Description */}
                <Row className="mt-4">
                    <Col>
                        <h5 className="text-primary mb-3">Description</h5>
                        <p style={{ lineHeight: "1.6", color: "#495057" }}>
                            {classData.description}
                        </p>
                        {classData.descriptionAr && (
                            <p style={{ lineHeight: "1.6", color: "#495057", direction: "rtl" }}>
                                {classData.descriptionAr}
                            </p>
                        )}
                    </Col>
                </Row>
                {/* Images */}
                {classData?.images?.length > 0 && (
                    <Row className="mt-4">
                        <Col>
                            <h5 className="text-primary mb-3">Gallery</h5>
                            <div
                                style={{
                                    display: "flex",
                                    gap: "15px",
                                    overflowX: "auto",
                                    paddingBottom: "10px",
                                }}
                            >
                                {classData.images.map((image, index) => (
                                    <img
                                        key={index}
                                        src={`${FILE_BASE_URL}/${image}`}
                                        alt={`Class Image ${index + 1}`}
                                        style={{
                                            width: "200px",
                                            height: "150px",
                                            objectFit: "cover",
                                            borderRadius: "8px",
                                            border: "1px solid #e9ecef",
                                            boxShadow: "0 2px 5px rgba(0,0,0,0.1)",
                                        }}
                                    />
                                ))}
                            </div>
                        </Col>
                    </Row>
                )}
            </ModalBody>
            <ModalFooter className="bg-light border-0">
                <Button
                    color="primary"
                    onClick={toggle}
                    style={{
                        borderRadius: "8px",
                        padding: "0.5rem 1.5rem",
                        fontWeight: "500",
                    }}
                >
                    Close
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default ClassDetailsModal;